"use client"

import { useState, useEffect, useCallback } from "react"
import { X, Download, Share } from "lucide-react"
import { Button } from "@/components/ui/button"

const DISMISSED_KEY = "pwa-install-dismissed"

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>
}

export function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showIOSGuide, setShowIOSGuide] = useState(false)
  const [dismissed, setDismissed] = useState(true)

  useEffect(() => {
    // Ne pas afficher si déjà en mode standalone (PWA installée)
    if (window.matchMedia("(display-mode: standalone)").matches) return
    // @ts-expect-error — Safari iOS propriété non standard
    if (window.navigator.standalone === true) return

    // Ne pas afficher si déjà refusé
    if (localStorage.getItem(DISMISSED_KEY)) return

    setDismissed(false)

    // Android / Chrome : intercepter l'événement natif
    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }
    window.addEventListener("beforeinstallprompt", handler)

    // iOS Safari : détecter et afficher le guide
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !("MSStream" in window)
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome|CriOS|FxiOS/.test(navigator.userAgent)
    if (isIOS && isSafari) {
      setShowIOSGuide(true)
    }

    return () => window.removeEventListener("beforeinstallprompt", handler)
  }, [])

  const dismiss = useCallback(() => {
    setDismissed(true)
    setDeferredPrompt(null)
    setShowIOSGuide(false)
    localStorage.setItem(DISMISSED_KEY, Date.now().toString())
  }, [])

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === "accepted") {
      dismiss()
    }
    setDeferredPrompt(null)
  }, [deferredPrompt, dismiss])

  // Rien à afficher
  if (dismissed || (!deferredPrompt && !showIOSGuide)) return null

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 p-3 sm:p-4">
      <div className="mx-auto max-w-lg rounded-xl bg-white border border-gray-200 shadow-lg p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#1B2A4A] flex items-center justify-center shrink-0">
              <Download className="size-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Installer l'application</p>
              <p className="text-xs text-gray-500">
                Accédez à Gestion de Chantiers comme une appli native
              </p>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Android / Chrome : bouton installer */}
        {deferredPrompt && (
          <div className="flex gap-2">
            <Button onClick={handleInstall} size="sm" className="flex-1 gap-1.5">
              <Download className="size-4" />
              Installer
            </Button>
            <Button onClick={dismiss} variant="outline" size="sm">
              Plus tard
            </Button>
          </div>
        )}

        {/* iOS Safari : guide étape par étape */}
        {showIOSGuide && !deferredPrompt && (
          <div className="space-y-2">
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">1</span>
              <span>Appuyez sur <Share className="size-4 inline text-[#007AFF] -mt-0.5" /> <strong>Partager</strong> en bas de Safari</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">2</span>
              <span>Choisissez <strong>"Sur l'écran d'accueil"</strong></span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">3</span>
              <span>Appuyez <strong>Ajouter</strong></span>
            </div>
            <Button onClick={dismiss} variant="outline" size="sm" className="w-full mt-1">
              J'ai compris
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
